import { Dispatch, ReactNode, SetStateAction } from 'react';
import { Key } from 'antd/lib/table/interface';

import { TravelMethod, TravelPackage, HealthDocument } from 'leash/types';
import { UserCapabilities } from 'utilities/const';

export type Nullable<T> = T | null | undefined;

export type Id = Nullable<number>;

export type UseStateSetter<T> = Dispatch<SetStateAction<T>>;

export interface User {
  email?: string;
  firstName: string;
  lastName: string;
  id?: Id;
}

export interface Credentials {
  email: string;
  password: string;
}

export type UserCapabilitiesTypes =
  | typeof UserCapabilities.UseLeashApp
  | typeof UserCapabilities.UsePricingApp
  | typeof UserCapabilities.UsePupDocsApp
  | typeof UserCapabilities.UseDogParkApp;

export interface BreadcrumbItem {
  key: string;
  label: string | ReactNode;
  path?: string;
}

export interface State {
  id: number;
  name: string;
  abbr: string;
}

export interface BlackoutDate {
  id: number;
  travelId: number;
  blackoutDate: string;
}

export interface Person {
  id: number;
  address: string;
  stateAbbr: string;
  city: string;
  countryAbbr: 'US' | 'CA';
  phone: string;
  email: string;
  postalCode: string;
  blackoutDates?: BlackoutDate[];
  airports?: Airport[];
}

export interface Address {
  city: string;
  address: string;
  address2?: string;
  stateAbbr?: string;
  postalCode: string;
  state?: string;
  country?: string;
}

export interface Customer extends Person {
  firstName: string;
  lastName: string;
  kustomerId?: string;
}

export interface Breeder extends Person {
  name: string;
  breederTier: string;
}

export interface Puppy {
  id: number;
  name: string;
  breederName?: string;
  dateOfBirth: string;
  dateAvailable: string;
  breed: string;
  lastWeighedAt: string;
  weightRange: string;
  microchipRfid: string;
  soldDate: string;
  gender?: string;
  documents?: HealthDocument[];
}

export interface BaseTravelProposal {
  departureTime?: string | null;
  arrivalTime?: string | null;
  id?: number;
}

export interface GroundTravelProposal extends BaseTravelProposal {
  groundTransportationCompanyId: string;
  pickupLocationId: string;
}

export interface CrateSize {
  name: string;
  id: number;
}

export interface TransporterCompany {
  id: number;
  name: string;
  phone: string;
  position?: number;
  additionalInformation?: string;
}

export interface ChaperoneCompany {
  id: number;
  name: string;
  isActive: boolean;
  phone?: string;
}

export interface Airline {
  id: number;
  name: string;
  phone?: string;
  additionalInformation?: string;
}

export type Binary = 0 | 1;

export interface Airport {
  name: string;
  code: string;
  id: number;
  stateId: number;
  isInternational?: Binary;
  canReceiveCargoShipments?: Binary;
  hasWeatherRestrictions?: Binary;
  travelMethods?: TravelMethod[];
}

export interface CharterTravelProposal extends BaseTravelProposal {
  crateSizeId: number;
  firstMileTransporterCompanyId: string;
  lastMileTransporterCompanyId: string;
  dropOffLocation: string;
  pickupLocationId: string;
  originAirportId: string;
  destinationAirportId: string;
}

export interface CargoTravelProposal extends BaseTravelProposal {
  originAirportId: string;
  destinationAirportId: string;
  airlineId: number;
  dropOffLocation: string;
  firstMileTransporterCompanyId: string;
  crateSizeId: number;
  pickupLocationId: string;
}

export interface ChaperoneTravelProposal extends BaseTravelProposal {
  puppyChaperoneCompanyId: number;
  originAirportId: string;
  destinationAirportId: string;
  dropOffLocation: string;
}

export interface MeetupTravelProposal extends BaseTravelProposal {
  meetupLocation: string;
}

export interface LocalDeliveryProposal extends BaseTravelProposal {
  transporterCompanyId: number;
}

export interface HubPassThruProposal extends BaseTravelProposal {
  crateSizeId: number;
  firstMileTransporterCompanyId: number;
  lastMileTransporterCompanyId: number;
  hubAirportId: number;
  dropOffLocation: string;
}

export type TravelProposal =
  | BaseTravelProposal
  | CharterTravelProposal
  | GroundTravelProposal
  | CargoTravelProposal
  | MeetupTravelProposal
  | ChaperoneTravelProposal
  | LocalDeliveryProposal
  | HubPassThruProposal;

export interface RejectionReason {
  id: number;
  name: string;
  travelEventTypeId: number;
}

export interface NoteMessage {
  id: number;
  message: string;
  createdAt: string;
  author: {
    firstName: string;
    lastName: string;
  };
}

export interface TravelPlan {
  address: Address;
  orderId: number;
  leadId: number;
  customer: Customer;
  purchasedAt: string;
  travelPackage: TravelPackage;
  breeder: Breeder;
  puppy: Puppy;
  travelSequences?: TravelSequence[];
  key?: number;
}

export type FetchHeaders = Record<string, string>;

export interface Facet {
  text: string;
  value: string;
}

export type FacetList = Record<string, Facet[]>;

export type TableFilter = Record<string, Key[] | null>;

export type AppNames = 'Leash' | 'PupDocs' | 'Kennel' | 'Pricing' | 'DogPark';

export interface FlightSequence extends BaseSequence {
  airlineId: number;
  airline?: Airline;
  arrivalAirportId: number;
  arrivalTime: Nullable<string>;
  departureAirportId: number;
  departureTime: Nullable<string>;
  flightAirWaybill: Nullable<string>;
  flightConfirmation: Nullable<string>;
  flightNumber: Nullable<string>;
  travelSequenceTypeSlug: Nullable<string>;
  airlinePhone?: Nullable<string>;
  airlineAdditionalInformation?: Nullable<string>;
}

export interface GroundTransportationSequence extends BaseSequence {
  additionalPhone?: string;
  arrivalTime: Nullable<string>;
  departureTime: Nullable<string>;
  driverName: string;
  groundTransportationCompanyId: number | null;
  travelSequenceTypeSlug: Nullable<string>;
  groundTransportationCompanyAdditionalInformation?: Nullable<string>;
}

export interface ChaperoneTransportationSequence extends BaseSequence {
  additionalPhone?: string;
  puppyChaperoneCompanyId?: Nullable<number>;
  flightSequence?: FlightSequence;
  travelSequenceTypeSlug?: Nullable<string>;
  chaperonePhone?: Nullable<string>;
}

export interface MeetupSequence extends BaseSequence {
  address?: string;
  address2?: string;
  city?: string;
  departureTime?: Nullable<string>;
  pickupLocationName?: Nullable<string>;
  pickupTime?: Nullable<string>;
  postalCode?: Nullable<number>;
  stateId?: number;
  exchangedNumbers?: Nullable<number | boolean>;
  accountAdjusted?: Nullable<number>;
  travelSequenceTypeSlug?: Nullable<string>;
}

export interface TransporterSequence extends BaseSequence {
  additionalPhone: string;
  arrivalTime: Nullable<string>;
  departureTime: Nullable<string>;
  transporterCompanyId: number;
  travelSequenceTypeSlug: Nullable<string>;
}

export enum SequenceTypesIds {
  GroundTransportationSequence = 1,
  FlightSequence = 2,
  TransporterSequence = 3,
  MeetupSequence = 4,
  PuppyChaperoneFlightSequence = 5,
}

export type GroundTransportationSequencePayload = {
  groundTransportation: GroundTransportationSequence;
};

export type FlightSequencePayload = {
  flight: FlightSequence;
};

export type ChaperoneTransportationSequencePayload = {
  puppyChaperoneFlightSequence: ChaperoneTransportationSequence;
};

export type MeetupSequencePayload = {
  meetup: MeetupSequence;
};

export type TransporterSequencePayload = {
  transporter: TransporterSequence;
};

export type SequencePayload =
  | GroundTransportationSequencePayload
  | FlightSequencePayload
  | ChaperoneTransportationSequencePayload
  | MeetupSequencePayload
  | TransporterSequencePayload;

export const sequenceTypeBySlug: { [key: string]: SequenceTypesIds } = {
  flight: SequenceTypesIds.FlightSequence,
  'ground-transportation': SequenceTypesIds.GroundTransportationSequence,
  'chaperone-flight': SequenceTypesIds.PuppyChaperoneFlightSequence,
  meetup: SequenceTypesIds.MeetupSequence,
  transporter: SequenceTypesIds.TransporterSequence,
};

export const sequenceSlugByType = {
  [SequenceTypesIds.FlightSequence]: 'flight',
  [SequenceTypesIds.GroundTransportationSequence]: 'ground-transportation',
  [SequenceTypesIds.PuppyChaperoneFlightSequence]: 'chaperone-flight',
  [SequenceTypesIds.MeetupSequence]: 'meetup',
  [SequenceTypesIds.TransporterSequence]: 'transporter',
};

export type EmptyTravelSequence = {
  travelSequenceId: number;
  detailSequenceId?: Id;
  travelSequenceTypeSlug: string;
  isNew?: boolean;
  isDeleting?: boolean;
};

export type SequenceChangesType =
  | 'GroundTransportation'
  | 'Airline'
  | 'Chaperone'
  | 'Transporter';

export type TravelSequence =
  | ChaperoneTransportationSequence
  | FlightSequence
  | GroundTransportationSequence
  | MeetupSequence
  | TransporterSequence
  | EmptyTravelSequence;

interface BaseSequence {
  isNew?: boolean;
  isDeleting?: boolean;
  travelSequenceId?: Id;
  detailSequenceId?: Id;
}

export const twentyFourHours = 24;
